/**
 * JavaScriptExecutor - Executes JavaScript code using native eval
 * 
 * Uses a sandboxed approach to execute JavaScript code safely in the browser.
 * Captures console.log output and handles errors gracefully.
 */

class JavaScriptExecutor {
  /**
   * Execute JavaScript code
   * 
   * @param {string} code - The JavaScript code to execute
   * @param {string} stdin - Input to provide (available as inputLines array)
   * @param {number} timeout - Maximum execution time in ms
   * @returns {Promise<{success: boolean, output: string, error: string|null, executionTime: number}>}
   */
  static async execute(code, stdin = '', timeout = 5000) {
    return new Promise((resolve) => {
      const startTime = Date.now();
      let output = '';
      let timedOut = false;

      // Set up timeout
      const timeoutId = setTimeout(() => {
        timedOut = true;
        resolve({
          success: false,
          output: output,
          error: `Execution timed out after ${timeout}ms. Your code might have an infinite loop.`,
          executionTime: timeout
        });
      }, timeout);

      try {
        // Create a sandboxed environment
        const inputLines = stdin.split('\n');
        let inputIndex = 0;

        // Custom console that captures output
        const customConsole = {
          log: (...args) => {
            output += args.map(arg => this.stringify(arg)).join(' ') + '\n';
          },
          error: (...args) => {
            output += 'Error: ' + args.map(arg => this.stringify(arg)).join(' ') + '\n';
          },
          warn: (...args) => {
            output += 'Warning: ' + args.map(arg => this.stringify(arg)).join(' ') + '\n';
          },
          info: (...args) => {
            output += args.map(arg => this.stringify(arg)).join(' ') + '\n';
          }
        };

        // Input function for reading stdin
        const readInput = () => {
          if (inputIndex < inputLines.length) {
            return inputLines[inputIndex++];
          }
          return '';
        };

        // Print function (alias for console.log)
        const print = (...args) => customConsole.log(...args);

        // Create the sandboxed execution context
        const sandboxCode = `
          (function(console, print, input, prompt) {
            "use strict";
            ${code}
          })
        `;

        // Execute the code
        const sandboxedFunction = eval(sandboxCode);
        const result = sandboxedFunction(customConsole, print, readInput, readInput);

        // Handle async results
        if (result instanceof Promise) {
          result
            .then(() => {
              if (!timedOut) {
                clearTimeout(timeoutId);
                resolve({
                  success: true,
                  output: output.trim(),
                  error: null,
                  executionTime: Date.now() - startTime
                });
              }
            })
            .catch((err) => {
              if (!timedOut) {
                clearTimeout(timeoutId);
                resolve({
                  success: false,
                  output: output.trim(),
                  error: this.parseError(err),
                  executionTime: Date.now() - startTime
                });
              }
            });
        } else {
          if (!timedOut) {
            clearTimeout(timeoutId);
            resolve({
              success: true,
              output: output.trim(),
              error: null,
              executionTime: Date.now() - startTime
            });
          }
        }
      } catch (err) {
        if (!timedOut) {
          clearTimeout(timeoutId);
          resolve({
            success: false,
            output: output.trim(),
            error: this.parseError(err),
            executionTime: Date.now() - startTime
          });
        }
      }
    });
  }

  /**
   * Stringify any value for output
   */
  static stringify(value) {
    if (value === undefined) return 'undefined';
    if (value === null) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'function') return '[Function]';
    if (Array.isArray(value)) {
      return '[' + value.map(v => this.stringify(v)).join(', ') + ']';
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Object]';
      }
    }
    return String(value);
  }

  /**
   * Parse JavaScript error into user-friendly message
   */
  static parseError(err) {
    if (!err) return 'An unknown error occurred';
    
    const name = err.name || 'Error';
    const message = err.message || String(err);
    
    // Extract line number if available
    let lineInfo = '';
    if (err.stack) {
      const lineMatch = err.stack.match(/<anonymous>:(\d+):(\d+)/);
      if (lineMatch) {
        // Adjust line number (subtract for wrapper code)
        const line = parseInt(lineMatch[1]) - 3;
        if (line > 0) {
          lineInfo = ` (line ${line})`;
        }
      }
    }

    // Provide user-friendly messages for common errors
    switch (name) {
      case 'SyntaxError':
        return this.parseSyntaxError(message) + lineInfo;
      case 'ReferenceError':
        return this.parseReferenceError(message) + lineInfo;
      case 'TypeError':
        return this.parseTypeError(message) + lineInfo;
      case 'RangeError':
        return `RangeError: ${message}${lineInfo}`;
      default:
        return `${name}: ${message}${lineInfo}`;
    }
  }

  /**
   * Parse syntax errors
   */
  static parseSyntaxError(message) {
    if (message.includes('Unexpected end of input')) {
      return 'SyntaxError: Unexpected end of code. You might be missing a closing bracket, brace, or quote.';
    }
    if (message.includes('Unexpected token')) {
      return `SyntaxError: ${message}. Check for typos or missing punctuation.`;
    }
    if (message.includes('Unexpected identifier')) {
      return 'SyntaxError: Unexpected word. You might be missing a comma, semicolon, or operator.';
    }
    return `SyntaxError: ${message}`;
  }

  /**
   * Parse reference errors
   */
  static parseReferenceError(message) {
    const match = message.match(/(\w+) is not defined/);
    if (match) {
      return `ReferenceError: '${match[1]}' is not defined. Did you forget to declare this variable or did you misspell it?`;
    }
    return `ReferenceError: ${message}`;
  }

  /**
   * Parse type errors
   */
  static parseTypeError(message) {
    if (message.includes('is not a function')) {
      const match = message.match(/(\w+) is not a function/);
      if (match) {
        return `TypeError: '${match[1]}' is not a function. Make sure you're calling a function correctly.`;
      }
    }
    if (message.includes('Cannot read property') || message.includes('Cannot read properties')) {
      return `TypeError: ${message}. You might be trying to access a property of undefined or null.`;
    }
    if (message.includes('is not iterable')) {
      return `TypeError: ${message}. Make sure you're using a for...of loop with an array or iterable.`;
    }
    return `TypeError: ${message}`;
  }

  /**
   * Check if JavaScript executor is ready (always true for native eval)
   */
  static isReady() {
    return true;
  }
}

export default JavaScriptExecutor;